/**
 * Auszahlungen an den Creator je Zahlung statt je Rechnung (GELD-R3-4, GELD-R3-5, Korrekturrunde 3). Eine Creator-Rechnung an
 * die Agentur wird anteilig ausgezahlt (Teilzahlung der Marke im Modus „nach Zahlungseingang“), verrechnet oder mit Einbehalt
 * ausgezahlt; jede dieser Buchungen steht in invoice_payments mit eigenem Tag. Ist die Auszahlung als vollständig vermerkt
 * (admin_payment_confirmed_at, sonst paid_at), zählt der noch nicht gebuchte Rest an diesem Tag. So nennen Creator-Abrechnung
 * und KSK-Auswertung denselben Auszahlungstag und dieselben Teilbeträge. Rein und ohne Datenbank.
 */

export interface PayoutInvoiceRow {
  id: string;
  gross_cents: number | null;
  status: string;
  paid_at: string | null;
  admin_payment_confirmed_at?: string | null;
}

export interface PayoutPaymentRow {
  invoice_id: string;
  kind: string;
  amount_cents: number;
  paid_on: string;
  created_at?: string | null;
}

export interface PayoutPart {
  invoiceId: string;
  /** Tag der Zahlung bzw. des Vermerks */
  paidOn: string;
  /** Betrag brutto in der Währung der Rechnung */
  cents: number;
  /** Anteil an der Rechnung (0–1), für netto gerechnete Auswertungen wie die KSK */
  share: number;
  kind: "payment" | "offset" | "withholding" | "rest";
}

const PART_KINDS = new Set(["payment", "offset", "withholding"]);

/** Tag, an dem die Auszahlung als vollständig gilt: vermerkt, sonst bezahlt; null = noch offen */
export const payoutDoneOn = (i: Pick<PayoutInvoiceRow, "paid_at" | "admin_payment_confirmed_at">): string | null =>
  (i.admin_payment_confirmed_at ?? i.paid_at)?.slice(0, 10) ?? null;

/** Auszahlungen einer Creator-Rechnung: gebuchte Teile und der Rest am Tag des Vermerks. Stornierte Rechnungen zählen nicht. */
export function payoutParts(inv: PayoutInvoiceRow, payments: readonly PayoutPaymentRow[]): PayoutPart[] {
  const gross = Number(inv.gross_cents ?? 0);
  if (gross <= 0 || inv.status === "cancelled") return [];
  const mine = payments.filter((p) => p.invoice_id === inv.id);
  // Zahlung zurückgesetzt (Gegenbuchung): nur Buchungen danach zählen
  const stamp = (p: PayoutPaymentRow) => p.created_at ?? p.paid_on;
  const lastReversal = mine.filter((p) => p.kind === "reversal").map(stamp).sort().pop() ?? null;
  const out: PayoutPart[] = mine
    .filter((p) => PART_KINDS.has(p.kind) && Number(p.amount_cents) > 0 && (!lastReversal || stamp(p) > lastReversal))
    .sort((a, b) => a.paid_on.localeCompare(b.paid_on))
    .map((p) => ({ invoiceId: inv.id, paidOn: p.paid_on.slice(0, 10), cents: Number(p.amount_cents), share: Number(p.amount_cents) / gross, kind: p.kind as PayoutPart["kind"] }));
  const covered = out.reduce((a, p) => a + p.cents, 0);
  const done = payoutDoneOn(inv);
  if (done && gross - covered > 0) out.push({ invoiceId: inv.id, paidOn: done, cents: gross - covered, share: (gross - covered) / gross, kind: "rest" });
  return out;
}

/** Noch nicht ausgezahlt (brutto): nach dem Vermerk nichts, sonst brutto minus gebuchte Teile */
export function payoutOpenCents(inv: PayoutInvoiceRow, payments: readonly PayoutPaymentRow[]): number {
  if (inv.status === "cancelled" || payoutDoneOn(inv)) return 0;
  const gross = Number(inv.gross_cents ?? 0);
  return Math.max(0, gross - payoutParts(inv, payments).reduce((a, p) => a + p.cents, 0));
}
